/**
 * Intelligence primitives — one shared vocabulary for showing how the platform
 * reached a conclusion.
 *
 * The housing programme already computes rule scores, partner match reasons,
 * document extraction confidence and model output, but each surface used to
 * render them differently (or not at all). These components render them the
 * same way in the citizen portal and the staff console.
 *
 * Every output that a machine produced carries a ProvenanceChip, so a reader
 * can always tell a live model from a deterministic fallback, and an indicative
 * referral from an approval.
 */
import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { useI18n, type Localized } from './i18n.js';

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Honesty vocabulary — eight labels, defined once
// ---------------------------------------------------------------------------

/**
 * The complete set of provenance labels used across the platform. Before this
 * existed the same idea was spelled "simulated", "indicative", "MVP" and
 * "seeded" on different screens; anything machine-derived now picks exactly one
 * of these.
 */
export type ProvenanceKind =
  | 'live_ai'
  | 'deterministic'
  | 'indicative'
  | 'seeded'
  | 'simulated'
  | 'mock_icp'
  | 'concept_image'
  | 'human_verified';

interface ProvenanceDefinition {
  label: Localized;
  /** Long form shown in an AiPanel disclosure. */
  detail: Localized;
  tone: 'info' | 'neutral' | 'caution' | 'success';
}

export const PROVENANCE: Record<ProvenanceKind, ProvenanceDefinition> = {
  live_ai: {
    label: { en: 'Live AI', ar: 'ذكاء اصطناعي مباشر' },
    detail: {
      en: 'Generated by a language model at the time you asked. Review before relying on it.',
      ar: 'أُنتج بواسطة نموذج لغوي عند الطلب. راجعه قبل الاعتماد عليه.',
    },
    tone: 'info',
  },
  deterministic: {
    label: { en: 'Deterministic', ar: 'قالب ثابت' },
    detail: {
      en: 'Produced by a fixed rule or template, not a model. The same input always gives the same output.',
      ar: 'أُنتج بقاعدة أو قالب ثابت وليس بنموذج. المدخل نفسه يعطي المخرج نفسه دائماً.',
    },
    tone: 'neutral',
  },
  indicative: {
    label: { en: 'Indicative referral', ar: 'إحالة استرشادية' },
    detail: {
      en: 'A suggestion to help you compare. It is not an approval, offer or commitment from the partner.',
      ar: 'اقتراح يساعدك على المقارنة. وليس موافقة أو عرضاً أو التزاماً من الجهة.',
    },
    tone: 'caution',
  },
  seeded: {
    label: { en: 'Seeded registry', ar: 'سجل تجريبي' },
    detail: {
      en: 'Read from a prepared registry rather than a live partner feed.',
      ar: 'مقروء من سجل مُعد سلفاً وليس من تغذية مباشرة.',
    },
    tone: 'caution',
  },
  simulated: {
    label: { en: 'Simulated rail', ar: 'مسار محاكى' },
    detail: {
      en: 'Runs on a platform-controlled rail with a full audit trail; no external vendor was called.',
      ar: 'يعمل على مسار تتحكم به المنصة مع سجل تدقيق كامل؛ لم يتم استدعاء أي مورد خارجي.',
    },
    tone: 'caution',
  },
  mock_icp: {
    label: { en: 'Mock ICP', ar: 'بيانات هوية تجريبية' },
    detail: {
      en: 'Household facts come from a mock Identity and Citizenship profile, not a live government read.',
      ar: 'بيانات الأسرة من ملف هوية وجنسية تجريبي وليست قراءة حكومية مباشرة.',
    },
    tone: 'caution',
  },
  concept_image: {
    label: { en: 'Concept only', ar: 'تصور فقط' },
    detail: {
      en: 'A visual idea to discuss with your consultant. It is not an engineering drawing or an approval.',
      ar: 'فكرة بصرية لمناقشتها مع الاستشاري. وليست مخططاً هندسياً أو موافقة.',
    },
    tone: 'caution',
  },
  human_verified: {
    label: { en: 'Human-verified', ar: 'مُتحقق بشرياً' },
    detail: {
      en: 'A ministry officer reviewed and confirmed this value.',
      ar: 'راجع موظف في الوزارة هذه القيمة وأكدها.',
    },
    tone: 'success',
  },
};

const TONE_CHIP: Record<ProvenanceDefinition['tone'], string> = {
  info: 'border-seablue-600/35 bg-seablue-50 text-seablue-800',
  neutral: 'border-aeblack-200 bg-aeblack-50 text-ink-soft',
  caution: 'border-camel-600/35 bg-camel-50 text-camel-700',
  success: 'border-aegreen-600/35 bg-aegreen-50 text-approve',
};

/**
 * A small, consistent badge naming where a value came from. `model` is appended
 * for live AI so the reader knows which model spoke.
 */
export function ProvenanceChip({
  kind,
  model,
  className,
}: {
  kind: ProvenanceKind;
  model?: string | null;
  className?: string;
}) {
  const { l } = useI18n();
  const def = PROVENANCE[kind];
  return (
    <span
      className={cx(
        'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black tracking-wide',
        TONE_CHIP[def.tone],
        className,
      )}
      title={l(def.detail)}
    >
      <span aria-hidden className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current" />
      <span className="truncate">{l(def.label)}</span>
      {kind === 'live_ai' && model ? (
        <span className="truncate font-mono font-bold opacity-80" dir="ltr">
          {model}
        </span>
      ) : null}
    </span>
  );
}

/** Resolve the two flags every housing AI response carries into one chip kind. */
export function aiProvenanceKind(simulated: boolean | null | undefined): ProvenanceKind {
  return simulated ? 'deterministic' : 'live_ai';
}

// ---------------------------------------------------------------------------
// FactorBars — why a case ranks where it does
// ---------------------------------------------------------------------------

export interface IntelligenceFactor {
  id: string;
  label: Localized;
  points: number;
}

export type PriorityBand = 'low' | 'medium' | 'high' | 'urgent';

const BAND_LABEL: Record<PriorityBand, Localized> = {
  urgent: { en: 'Urgent', ar: 'عاجلة' },
  high: { en: 'High', ar: 'مرتفعة' },
  medium: { en: 'Medium', ar: 'متوسطة' },
  low: { en: 'Low', ar: 'منخفضة' },
};

const BAND_CHIP: Record<PriorityBand, string> = {
  urgent: 'border-aered-600/40 bg-aered-50 text-reject',
  high: 'border-camel-600/40 bg-camel-50 text-camel-700',
  medium: 'border-seablue-600/40 bg-seablue-50 text-seablue-800',
  low: 'border-aeblack-200 bg-aeblack-50 text-ink-soft',
};

const BAND_BAR: Record<PriorityBand, string> = {
  urgent: 'bg-aered-600',
  high: 'bg-camel-600',
  medium: 'bg-seablue-600',
  low: 'bg-aeblack-400',
};

/** Band label on its own, for table cells and queue rows. */
export function PriorityBandPill({
  band,
  score,
  className,
}: {
  band: PriorityBand;
  score?: number | null;
  className?: string;
}) {
  const { l } = useI18n();
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black tracking-wide',
        BAND_CHIP[band],
        className,
      )}
    >
      {l(BAND_LABEL[band])}
      {score != null ? (
        <span className="font-mono tabular-nums opacity-80" dir="ltr">
          {score}
        </span>
      ) : null}
    </span>
  );
}

/**
 * Renders a weighted score as its contributing factors, so a decision about
 * queue order is legible rather than a bare number. `hint` carries the
 * "what would move this up" note the citizen-facing preview uses.
 */
export function FactorBars({
  factors,
  band,
  score,
  summary,
  hint,
  caption,
  className,
}: {
  factors: IntelligenceFactor[];
  band: PriorityBand;
  score: number;
  summary?: Localized | null;
  hint?: Localized | null;
  caption?: Localized | null;
  className?: string;
}) {
  const { l } = useI18n();
  const max = Math.max(1, ...factors.map((factor) => factor.points));

  return (
    <div className={cx('min-w-0', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PriorityBandPill band={band} score={score} />
        {caption ? (
          <span className="text-[10px] font-bold uppercase tracking-wider text-ink-faint">
            {l(caption)}
          </span>
        ) : null}
      </div>

      {factors.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {factors.map((factor) => (
            <li key={factor.id} className="min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 flex-1 truncate text-xs font-bold text-ink" title={l(factor.label)}>
                  {l(factor.label)}
                </span>
                <span className="shrink-0 font-mono text-[11px] font-black tabular-nums text-ink-soft" dir="ltr">
                  +{factor.points}
                </span>
              </div>
              <div
                className="mt-1 h-1.5 overflow-hidden rounded-full bg-aeblack-100"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={max}
                aria-valuenow={factor.points}
                aria-label={l(factor.label)}
              >
                <div
                  className={cx('h-full rounded-full', BAND_BAR[band])}
                  style={{ width: `${Math.round((factor.points / max) * 100)}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs leading-6 text-ink-soft">
          {l({
            en: 'No ranking factors recorded yet.',
            ar: 'لا توجد عوامل ترتيب مسجّلة بعد.',
          })}
        </p>
      )}

      {summary ? <p className="mt-3 text-xs leading-6 text-ink-soft">{l(summary)}</p> : null}

      {hint ? (
        <p className="mt-2 rounded-lg border border-seablue-600/25 bg-seablue-50 px-3 py-2 text-[11px] leading-5 font-semibold text-seablue-800">
          {l(hint)}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReasonList + MatchMeter — why a partner was suggested
// ---------------------------------------------------------------------------

export interface IntelligenceReason {
  code: string;
  label: Localized;
  value?: string | number | boolean;
}

function reasonValueText(value: IntelligenceReason['value'], lang: 'en' | 'ar'): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'boolean') {
    return value ? (lang === 'ar' ? 'نعم' : 'Yes') : lang === 'ar' ? 'لا' : 'No';
  }
  return String(value);
}

/** The reasons behind a match, as scannable chips rather than a JSON dump. */
export function ReasonList({
  reasons,
  title,
  className,
}: {
  reasons: IntelligenceReason[];
  title?: Localized | null;
  className?: string;
}) {
  const { l, lang } = useI18n();
  if (reasons.length === 0) return null;

  return (
    <div className={cx('min-w-0', className)}>
      {title ? (
        <div className="text-[10px] font-black uppercase tracking-wider text-ink-faint">{l(title)}</div>
      ) : null}
      <ul className={cx('flex flex-wrap gap-1.5', title && 'mt-2')}>
        {reasons.map((reason) => {
          const value = reasonValueText(reason.value, lang);
          return (
            <li
              key={reason.code}
              className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-aeblack-100 bg-white px-2.5 py-1"
            >
              <span className="truncate text-[11px] font-bold text-ink">{l(reason.label)}</span>
              {value ? (
                <span className="shrink-0 font-mono text-[10px] font-black text-ink-faint" dir="ltr">
                  {value}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A 0–100 match score as a ring, paired with ReasonList on partner cards. */
export function MatchMeter({
  score,
  label,
  size = 44,
  className,
}: {
  score: number;
  label?: Localized | null;
  size?: number;
  className?: string;
}) {
  const { l } = useI18n();
  const clamped = Math.max(0, Math.min(100, Math.round(score)));
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const tone = clamped >= 75 ? 'text-approve' : clamped >= 50 ? 'text-seablue-700' : 'text-camel-700';

  return (
    <div className={cx('inline-flex items-center gap-2.5', className)}>
      <span
        className="relative inline-grid shrink-0 place-items-center"
        style={{ width: size, height: size }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        aria-label={l(label ?? { en: 'Match score', ar: 'درجة المطابقة' })}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth="3"
            className="stroke-aeblack-100"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth="3"
            strokeLinecap="round"
            className={cx('stroke-current', tone)}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - clamped / 100)}
          />
        </svg>
        <span
          className={cx('absolute font-mono text-[11px] font-black tabular-nums', tone)}
          dir="ltr"
        >
          {clamped}
        </span>
      </span>
      {label ? <span className="text-[11px] font-bold text-ink-soft">{l(label)}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfidenceMeter — how sure the extractor is, and where review kicks in
// ---------------------------------------------------------------------------

/**
 * Extraction confidence with the human-review threshold drawn on the track, so
 * a low number reads as "a person will confirm this" rather than as an error.
 */
export function ConfidenceMeter({
  confidence,
  threshold = 0.7,
  needsReview,
  className,
}: {
  /** 0–1 as stored, or 0–100; both are accepted. */
  confidence: number;
  threshold?: number;
  needsReview?: boolean;
  className?: string;
}) {
  const { l } = useI18n();
  const ratio = confidence > 1 ? confidence / 100 : confidence;
  const pct = Math.max(0, Math.min(100, Math.round(ratio * 100)));
  const thresholdPct = Math.max(0, Math.min(100, Math.round(threshold * 100)));
  const below = ratio < threshold;

  return (
    <div className={cx('min-w-0', className)}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-black uppercase tracking-wider text-ink-faint">
          {l({ en: 'Extraction confidence', ar: 'ثقة الاستخراج' })}
        </span>
        <span className="font-mono text-[11px] font-black tabular-nums text-ink" dir="ltr">
          {pct}%
        </span>
      </div>
      <div
        className="relative mt-1.5 h-2 overflow-hidden rounded-full bg-aeblack-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={l({ en: 'Extraction confidence', ar: 'ثقة الاستخراج' })}
      >
        <div
          className={cx('h-full rounded-full', below ? 'bg-camel-600' : 'bg-aegreen-600')}
          style={{ width: `${pct}%` }}
        />
        <span
          aria-hidden
          className="absolute top-0 h-full w-0.5 bg-ink/45"
          style={{ insetInlineStart: `${thresholdPct}%` }}
        />
      </div>
      <p className="mt-1.5 text-[11px] leading-5 font-semibold text-ink-soft">
        {needsReview ?? below
          ? l({
              en: 'Below the review threshold — a ministry officer will confirm these values.',
              ar: 'أقل من حد المراجعة — سيؤكد موظف في الوزارة هذه القيم.',
            })
          : l({
              en: 'Above the review threshold — values read cleanly.',
              ar: 'أعلى من حد المراجعة — تمت القراءة بوضوح.',
            })}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AiPanel — a machine-produced conclusion with its reasoning and provenance
// ---------------------------------------------------------------------------

/**
 * Wraps any machine output with its reasoning and a mandatory provenance chip.
 * The "how this was produced" disclosure is a real <details>, so keyboard and
 * screen-reader users reach the same explanation as everyone else.
 */
export function AiPanel({
  title,
  bullets,
  provenance,
  model,
  children,
  actions,
  tone = 'info',
  className,
}: {
  title: Localized;
  bullets?: Localized[];
  provenance: ProvenanceKind;
  model?: string | null;
  children?: ReactNode;
  actions?: ReactNode;
  tone?: 'info' | 'quiet';
  className?: string;
}) {
  const { l } = useI18n();
  const def = PROVENANCE[provenance];

  return (
    <section
      className={cx(
        'rounded-xl border p-4',
        tone === 'info' ? 'border-seablue-600/25 bg-seablue-50/60' : 'border-aeblack-100 bg-white',
        className,
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2.5">
          <span
            aria-hidden
            className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-seablue-600/30 bg-white text-seablue-800"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3l1.7 4.6L18 9.2l-4.3 1.6L12 15.5l-1.7-4.7L6 9.2l4.3-1.6z" />
            </svg>
          </span>
          <h3 className="min-w-0 text-sm font-black tracking-tight text-ink">{l(title)}</h3>
        </div>
        <ProvenanceChip kind={provenance} model={model} />
      </div>

      {bullets && bullets.length > 0 ? (
        <ul className="mt-3 space-y-1.5">
          {bullets.map((bullet, index) => (
            <li key={index} className="flex gap-2 text-xs leading-6 text-ink-soft">
              <span aria-hidden className="mt-2 inline-block h-1 w-1 shrink-0 rounded-full bg-ink-faint" />
              <span className="min-w-0">{l(bullet)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {children ? <div className="mt-3">{children}</div> : null}

      <details className="mt-3 border-t border-ink/10 pt-2.5">
        <summary className="cursor-pointer text-[11px] font-black text-seablue-800">
          {l({ en: 'How this was produced', ar: 'كيف أُنتج هذا' })}
        </summary>
        <p className="mt-1.5 text-[11px] leading-5 text-ink-soft">{l(def.detail)}</p>
      </details>

      {actions ? <div className="mt-3 flex flex-wrap gap-2">{actions}</div> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// DecisionTrace — the hash-chained audit trail
// ---------------------------------------------------------------------------

export interface TraceEntry {
  id?: string;
  action?: string;
  actor?: string;
  at?: string;
  [key: string]: unknown;
}

/**
 * Audit entries with the chain-integrity badge the trace endpoint already
 * returns. A broken chain is stated plainly rather than left for the reader to
 * infer from a boolean in a JSON blob.
 */
export function DecisionTrace({
  entries,
  chainOk,
  limit = 8,
  className,
}: {
  entries: TraceEntry[];
  chainOk: boolean | null | undefined;
  limit?: number;
  className?: string;
}) {
  const { l, lang } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? entries : entries.slice(0, limit);

  return (
    <div className={cx('min-w-0', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] font-black uppercase tracking-wider text-ink-faint">
          {l({ en: 'Audit trail', ar: 'سجل التدقيق' })}
        </div>
        <span
          className={cx(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black',
            chainOk === false
              ? 'border-aered-600/40 bg-aered-50 text-reject'
              : chainOk === true
                ? 'border-aegreen-600/35 bg-aegreen-50 text-approve'
                : 'border-aeblack-200 bg-aeblack-50 text-ink-soft',
          )}
        >
          {chainOk === false
            ? l({ en: 'Chain broken', ar: 'السلسلة مكسورة' })
            : chainOk === true
              ? l({ en: 'Chain verified', ar: 'السلسلة موثّقة' })
              : l({ en: 'Chain unknown', ar: 'السلسلة غير معروفة' })}
        </span>
      </div>

      {shown.length === 0 ? (
        <p className="mt-2 text-xs text-ink-soft">
          {l({ en: 'No audit entries yet.', ar: 'لا توجد مدخلات تدقيق بعد.' })}
        </p>
      ) : (
        <ol className="mt-2.5 space-y-1.5">
          {shown.map((entry, index) => (
            <li
              key={entry.id ?? index}
              className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-aeblack-100 pb-1.5 text-[11px] last:border-0"
            >
              <span className="font-bold text-ink">{String(entry.action ?? '—')}</span>
              {entry.actor ? (
                <span className="text-ink-soft" dir="ltr">
                  {String(entry.actor)}
                </span>
              ) : null}
              {entry.at ? (
                <span className="ms-auto font-mono tabular-nums text-ink-faint" dir="ltr">
                  {formatTraceTime(String(entry.at), lang)}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      )}

      {entries.length > limit ? (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 cursor-pointer text-[11px] font-black text-seablue-800 underline underline-offset-2"
        >
          {expanded
            ? l({ en: 'Show less', ar: 'إظهار أقل' })
            : l({ en: `Show all ${entries.length}`, ar: `إظهار الكل (${entries.length})` })}
        </button>
      ) : null}
    </div>
  );
}

function formatTraceTime(iso: string, lang: 'en' | 'ar'): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(lang === 'ar' ? 'ar-AE' : 'en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    numberingSystem: 'latn',
  }).format(date);
}

// ---------------------------------------------------------------------------
// Shared small parts used by the guidance renderings
// ---------------------------------------------------------------------------

/**
 * A labelled key/value row. Used wherever a screen previously dumped raw object
 * keys — the label is bilingual and the value keeps its own direction.
 */
export function FactRow({
  label,
  value,
  ltr,
  provenance,
}: {
  label: Localized;
  value: ReactNode;
  ltr?: boolean;
  provenance?: ProvenanceKind;
}) {
  const { l } = useI18n();
  return (
    <div className="min-w-0 border-b border-aeblack-100 pb-1.5 last:border-0">
      <dt className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-ink-faint">
        <span className="truncate">{l(label)}</span>
        {provenance ? <ProvenanceChip kind={provenance} className="shrink-0 py-0" /> : null}
      </dt>
      <dd className="mt-0.5 text-xs font-bold text-ink" dir={ltr ? 'ltr' : undefined}>
        {value}
      </dd>
    </div>
  );
}

/**
 * A horizontal completeness meter with a caption — used for committee readiness
 * and intake progress so both read the same way.
 */
export function ReadinessMeter({
  done,
  total,
  label,
  blockers,
  className,
}: {
  done: number;
  total: number;
  label: Localized;
  blockers?: { id: string; label: Localized; detail?: Localized }[];
  className?: string;
}) {
  const { l } = useI18n();
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const complete = total > 0 && done >= total;
  const headingId = useId();

  return (
    <div className={cx('min-w-0', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span id={headingId} className="text-xs font-black text-ink">
          {l(label)}
        </span>
        <span className="font-mono text-[11px] font-black tabular-nums text-ink-soft" dir="ltr">
          {done}/{total}
        </span>
      </div>
      <div
        className="mt-1.5 h-2 overflow-hidden rounded-full bg-aeblack-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-labelledby={headingId}
      >
        <div
          className={cx('h-full rounded-full', complete ? 'bg-aegreen-600' : 'bg-camel-600')}
          style={{ width: `${pct}%` }}
        />
      </div>
      {blockers && blockers.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {blockers.map((blocker) => (
            <li key={blocker.id} className="flex gap-2 text-[11px] leading-5 text-ink-soft">
              <span aria-hidden className="mt-1.5 inline-block h-1 w-1 shrink-0 rounded-full bg-camel-600" />
              <span className="min-w-0">
                <span className="font-bold text-ink">{l(blocker.label)}</span>
                {blocker.detail ? <span> — {l(blocker.detail)}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
